import { AdminPageLayout } from '@/components/admin/AdminPageLayout';
import { AppointmentTypesTable } from '@/components/scheduling-rules/AppointmentTypesTable';
import { MentalModelView } from '@/components/scheduling-rules/MentalModelView';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useSuperAdmin } from '@/utils/use-super-admin';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import { Archive, Download, FileText, Loader2, Settings } from 'lucide-react';
import { useEffect } from 'react';

export const Route = createFileRoute('/_protected/admin/scheduling-rules')({
  component: SchedulingRules,
});

function SchedulingRules() {
  const { isSuperAdmin, isLoading: isAdminLoading } = useSuperAdmin();
  const router = useRouter();

  // Redirect non-super admins to dashboard
  useEffect(() => {
    if (!isAdminLoading && !isSuperAdmin) {
      router.navigate({ to: '/dashboard' });
    }
  }, [isAdminLoading, isSuperAdmin, router]);

  // Note: Export functionality not implemented
  // This is a UI placeholder for future feature development
  const handleBulkExport = (format: 'pdf' | 'zip' | 'json') => {
    // TODO: Implement actual export when requirements are defined
    alert(`Export as ${format.toUpperCase()} is not yet implemented`);
  };

  if (isAdminLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  if (!isSuperAdmin) {
    return null;
  }

  return (
    <AdminPageLayout
      title="Scheduling Rules"
      description="Understand how the AI agent makes scheduling decisions and manages appointment types across providers"
      icon={Settings}
      isSuperAdminPage={true}
      headerActions={
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline">
              <Archive className="h-4 w-4 mr-2" />
              Export All
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => handleBulkExport('pdf')}>
              <FileText className="h-4 w-4 mr-2" />
              Complete Documentation (PDF)
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleBulkExport('zip')}>
              <Archive className="h-4 w-4 mr-2" />
              All Data & Charts (ZIP)
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => handleBulkExport('json')}>
              <Download className="h-4 w-4 mr-2" />
              Rules Configuration (JSON)
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      }
    >
      <Tabs defaultValue="mental-model" className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="mental-model">Mental Model</TabsTrigger>
          <TabsTrigger value="appointment-types">Appointment Types</TabsTrigger>
        </TabsList>

        <TabsContent value="mental-model" className="mt-6">
          <MentalModelView />
        </TabsContent>

        <TabsContent value="appointment-types" className="mt-6">
          <AppointmentTypesTable />
        </TabsContent>
      </Tabs>
    </AdminPageLayout>
  );
}
